// Reusable Input — pairs a label, the input itself, and an optional
// error message under a single component so feature forms stay
// consistent across the dashboard. The label/input are linked via id so
// screen readers announce them together.

import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    ({ label, error, id, className = '', ...rest }, ref) => {
        const generatedId = useId();
        const inputId = id ?? generatedId;
        const describedBy = error ? `${inputId}-error` : undefined;

        const inputClasses = [
            'w-full rounded-lg border px-4 py-2.5 text-base',
            'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100',
            'placeholder:text-slate-400 dark:placeholder:text-slate-500',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400',
            error
                ? 'border-red-500 focus-visible:ring-red-400'
                : 'border-slate-300 dark:border-slate-700',
            className,
        ].join(' ');

        return (
            <div className="flex flex-col gap-1">
                {label && (
                    <label
                        htmlFor={inputId}
                        className="text-sm font-medium text-slate-700 dark:text-slate-300"
                    >
                        {label}
                    </label>
                )}
                <input
                    ref={ref}
                    id={inputId}
                    aria-invalid={Boolean(error) || undefined}
                    aria-describedby={describedBy}
                    className={inputClasses}
                    {...rest}
                />
                {error && (
                    <p id={describedBy} className="text-xs text-red-500">
                        {error}
                    </p>
                )}
            </div>
        );
    },
);
Input.displayName = 'Input';

export default Input;
